import { useQuery } from "@tanstack/react-query";
import { dashboardBFF } from "../axios-instances";
import { getEnv } from "src/config/index";
import { useFeatureToggle } from "src/api/featureToggling/useGetFeatureToggle";
import { FeatureToggleKey } from "src/api";
import { getServices } from "src/logs";
import { QueryClientScope } from "../types";
import { RecommendedAlertsListResponse } from "./types";

const { logger } = getServices();

// tip for testing locally: you can set REACT_APP_BACKEND_URL=http://localhost:5000 in .env.development
const refreshInterval =
  getEnv().REACT_APP_BE_REFRESH_INTERVAL ||
  (() => {
    logger.logError({ message: "REACT_APP_BE_REFRESH_INTERVAL environmental variable not set" });
    throw new Error("REACT_APP_BE_REFRESH_INTERVAL environmental variable not set");
  })();

export const getRecommendedAlertsList =
  (logsDetailedFeature?: boolean | undefined) => async () => {
    try {
      const uri = `/api/alarms/recommended-alerts`;

      const response = await dashboardBFF.get(uri, { timeout: 3000 });

      const RecommendedAlertsListResult = response.data as RecommendedAlertsListResponse;

      if (logsDetailedFeature) {
        logger.logTrace({
          message: "getRecommendedAlertsList - response",
          properties: {
            RecommendedAlertsList: JSON.stringify(RecommendedAlertsListResult.recommendedAlertList)
          }
        });
      }
      return RecommendedAlertsListResult;
    } catch (error) {
      const err = error as Error;
      if (!err.message.includes("204")) {
        logger.logError({
          message: "getRecommendedAlertsList - Exception",
          properties: {
            name: err.name,
            message: err.message,
            stack: err.stack || "unknown stack",
            cause: String(err.cause ?? "unknown cause")
          }
        });
      }
      throw error;
    }
  };

export const useGetRecommendedAlertsList = (enabled: boolean = true) => {
  const { data: logsDetailedFeature } = useFeatureToggle(FeatureToggleKey.LogsDetailed);

  return useQuery<RecommendedAlertsListResponse, Error>({
    queryKey: [QueryClientScope.RecommendedAlertsList],
    queryFn: getRecommendedAlertsList(logsDetailedFeature?.isEnabled),
    enabled,
    refetchOnWindowFocus: false,
    retry: 3, // Retry 3 times
    retryDelay: 3000, // Wait 3 seconds between each retry (in milliseconds)
    refetchInterval: parseInt(refreshInterval)
  });
};
